'use client';

import { type FC, type ChangeEvent, useContext, useState } from 'react';
import styles from './ScheduledQuestionnaire.module.css';
import { ScheduledQuestionnaireProps } from './ScheduledQuestionnaire.types';
import {
  Button,
  ButtonGroup,
  Text,
  EntitySelectorInputChips,
  Flex,
  Toggle,
} from '@/_components';
import { TEMPLATE_FREQUENCY_OPTIONS } from '@/_constants/questionnaireTemplates';
import {
  deleteTemplateScheduler,
  updateTemplateScheduler,
} from '@/_api/admin-template-schedulers';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';

export const ScheduledQuestionnaire: FC<ScheduledQuestionnaireProps> = ({
  questionnaireTemplate,
  loadScheduledQuestionnaires,
}) => {
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const [isLoading, setLoading] = useState<boolean>(false);

  if (!questionnaireTemplate?.id) return null;

  const findFrequencyText = (frequency: string): string => {
    const match = TEMPLATE_FREQUENCY_OPTIONS.find((c) => c.value === frequency);

    if (match) {
      return match.text.toLowerCase();
    }

    return '';
  };

  const onDelete = async () => {
    try {
      setLoading(true);

      await deleteTemplateScheduler({
        templateSchedulerId: questionnaireTemplate.id,
        token,
      });

      addToast('success', 'Template scheduler has been deleted successfully.');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
      loadScheduledQuestionnaires();
    }
  };

  const onChipsChange = async (chips: Array<number>) => {
    try {
      setLoading(true);

      await updateTemplateScheduler({
        params: {
          ...questionnaireTemplate,
          company_ids: chips,
        },
        token,
      });

      addToast('success', 'Entities have been updated successfully.');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
      loadScheduledQuestionnaires();
    }
  };

  const onToggleActive = async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      setLoading(true);

      await updateTemplateScheduler({
        params: {
          ...questionnaireTemplate,
          status: event.target.checked ? 'active' : 'inactive',
        },
        token,
      });

      addToast('success', 'Status has been updated successfully');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
      loadScheduledQuestionnaires();
    }
  };

  return (
    <Flex gap={6} padding={8} className={styles.scheduledQuestionnaire}>
      <Flex isColumn={true} gap={2}>
        <Text type="small" fontWeight="medium">
          Summary
        </Text>
        <Text type="small">
          <Text type="small">
            Send <b>{questionnaireTemplate.name}</b> template
            {questionnaireTemplate.year
              ? ` for ${questionnaireTemplate.year}`
              : ''}{' '}
            <b>{findFrequencyText(questionnaireTemplate.frequency)}</b>{' '}
            {questionnaireTemplate.frequency !== 'once'
              ? 'starting from'
              : 'on'}{' '}
            <b>{questionnaireTemplate.first_scheduled_date}</b>, due in{' '}
            <b>{questionnaireTemplate.days_until_due}</b> days.{' '}
          </Text>
        </Text>
      </Flex>
      <EntitySelectorInputChips
        label="Entities"
        preselectedEntityIds={questionnaireTemplate.company_ids}
        onChipsChange={onChipsChange}
      />
      <Flex isColumn={true} gap={2}>
        <Text type="small" fontWeight="medium">
          Active
        </Text>
        <Toggle
          isLabelHidden={true}
          label="Active"
          isDisabled={isLoading}
          isChecked={Boolean(questionnaireTemplate?.status === 'active')}
          onChange={onToggleActive}
          isSmall={true}
        />
      </Flex>
      <Flex isColumn={true} gap={2}>
        <Text type="small" fontWeight="medium">
          Delete
        </Text>
        <ButtonGroup>
          <Button
            onClick={onDelete}
            isDisabled={isLoading}
            isLoading={isLoading}
            intent="danger-secondary"
            size="tiny"
            value="Delete"
          />
        </ButtonGroup>
      </Flex>
    </Flex>
  );
};
